import { useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import {
  Alert,
  Checkbox,
  HStack,
  Input,
  OutlineDangerButton,
  PrimaryButton,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { AlertActions } from '~/components/common/AlertActions'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { DsMultiSelect } from '~/components/common/DsMultiSelect'
import { DsSelect } from '~/components/common/DsSelect'
import { enumSetValueAs } from '~/components/common/EnumSelect'
import {
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { RequiredMark } from '~/components/common/RequiredMark'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { workflowSectionLabelKey } from '~/models/workflowSection'
import {
  useCreateWorkflowAutoCreationRules,
  useDeleteWorkflowAutoCreationRule,
  useUpdateWorkflowAutoCreationRule,
} from '~/pages/AdminWorkflowTemplates/hooks/useWorkflowAutoCreationRules'
import {
  type AutoCreationRuleRow,
  type AutoCreationRuleTemplate,
  DAY_FILTER_LABEL_KEYS,
  DAY_FILTER_OPTIONS,
  LEAD_UNIT_LABEL_KEYS,
  LEAD_UNIT_OPTIONS,
  defaultLeadForSection,
  isScheduleEligibleSection,
  parseTimeOfDayHm,
  targetDaysLabelKey,
} from '~/pages/AdminWorkflowTemplates/models/autoCreationRule'
import {
  type AutoCreationRuleFormValues,
  autoCreationRuleFormValuesFromRow,
  createAutoCreationRuleFormSchema,
  defaultAutoCreationRuleFormValues,
} from '~/pages/AdminWorkflowTemplates/models/autoCreationRuleSchema'
import {
  type AutoCreationRuleErrorDisplay,
  mapAutoCreationRuleDeleteError,
  mapAutoCreationRuleSaveError,
} from '~/pages/AdminWorkflowTemplates/models/autoCreationRuleServerError'

const K = 'page:adminWorkflowTemplates.autoCreationRule'

const TRIGGER_SCHEDULE = 'schedule'
const TRIGGER_EVENT = 'event'

type Props = {
  isOpen: boolean
  /** undefined = 新規。 */
  row: AutoCreationRuleRow | undefined
  templates: readonly AutoCreationRuleTemplate[]
  onClose: () => void
  /** 入力を捨てて最新を読み直す。モーダルを閉じて一覧を取り直す。 */
  onReload: () => void
}

type Banner = Extract<AutoCreationRuleErrorDisplay, { kind: 'banner' }>

/** 自動起票ルールの追加・編集モーダル (AC-WFT-021 / 052 / 053 / 054)。 */
export const AutoCreationRuleModal = ({
  isOpen,
  row,
  templates,
  onClose,
  onReload,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const isEdit = row !== undefined
  const createRules = useCreateWorkflowAutoCreationRules()
  const updateRule = useUpdateWorkflowAutoCreationRule()
  const deleteRule = useDeleteWorkflowAutoCreationRule()
  const [banner, setBanner] = useState<Banner | undefined>(undefined)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false)
  const showErrorToast = useErrorToast()

  const {
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<AutoCreationRuleFormValues>({
    resolver: zodResolver(createAutoCreationRuleFormSchema(t)),
    defaultValues: isEdit
      ? autoCreationRuleFormValuesFromRow(row)
      : defaultAutoCreationRuleFormValues(),
  })

  const templateId = useWatch({ control, name: 'templateId' })
  const selectedTemplate = templates.find((tpl) => tpl.id === templateId)

  const templateLabel = (tpl: AutoCreationRuleTemplate): string => {
    const sectionKey = workflowSectionLabelKey(
      'adminWorkflowTemplates',
      tpl.section,
    )
    return t(`${K}.form.templateOption`, {
      name: localizeBuiltinWorkflowText(tpl.name, t),
      section: sectionKey !== undefined ? t(sectionKey) : '',
    })
  }
  const templateOptions = templates
    .filter((tpl) => isScheduleEligibleSection(tpl.section))
    .map((tpl) => ({ value: tpl.id, label: templateLabel(tpl) }))
  const areaOptions = AREA_CODE_OPTIONS.map((code) => ({
    value: String(code),
    label: convertAreaLabel(code),
  }))

  const targetDaysText = ((): string => {
    if (selectedTemplate === undefined) return t(`${K}.form.targetDaysPending`)
    const daysKey = targetDaysLabelKey(selectedTemplate.section)
    const sectionKey = workflowSectionLabelKey(
      'adminWorkflowTemplates',
      selectedTemplate.section,
    )
    return t(`${K}.form.targetDaysValue`, {
      days: daysKey !== undefined ? t(daysKey) : '-',
      section: sectionKey !== undefined ? t(sectionKey) : '-',
    })
  })()

  const showError = (display: AutoCreationRuleErrorDisplay): void => {
    if (display.kind === 'deleted') {
      onReload()
      showErrorToast({ title: t(display.messageKey) })
      return
    }
    if (display.kind === 'field') {
      setBanner(undefined)
      setError(display.field, {
        type: 'server',
        message: t(display.messageKey),
      })
      return
    }
    setBanner(display)
  }

  const onSubmit = async (
    values: AutoCreationRuleFormValues,
  ): Promise<void> => {
    setBanner(undefined)
    const schedule = {
      leadValue: Number(values.leadValue.trim()),
      leadUnit: values.leadUnit,
      fireTime: parseTimeOfDayHm(values.fireTimeHm),
      dayFilter: values.dayFilter,
    }
    try {
      if (isEdit) {
        await updateRule.mutateAsync({
          id: row.id,
          workflowTemplateId: values.templateId,
          areaCode: values.areaCodes[0],
          enabled: values.enabled,
          trigger: { case: 'schedule', value: schedule },
          expectedUpdatedAt: row.updatedAt,
        })
        onClose()
        showSuccessToast({ title: t(`${K}.toast.updated`) })
        return
      }
      const res = await createRules.mutateAsync({
        workflowTemplateId: values.templateId,
        areaCodes: AREA_CODE_OPTIONS.filter((code) =>
          values.areaCodes.includes(code),
        ),
        enabled: values.enabled,
        trigger: { case: 'schedule', value: schedule },
      })
      onClose()
      const skipped = res.skippedAreaCodes.length
      showSuccessToast({
        title: t(`${K}.toast.created`, { count: res.rules.length }),
        description:
          skipped > 0 ? t(`${K}.toast.skipped`, { count: skipped }) : undefined,
      })
    } catch (err) {
      showError(mapAutoCreationRuleSaveError(err, isEdit ? 'edit' : 'create'))
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (!isEdit) return
    try {
      await deleteRule.mutateAsync({
        id: row.id,
        expectedUpdatedAt: row.updatedAt,
      })
      setIsDeleteConfirmOpen(false)
      onClose()
      showSuccessToast({ title: t(`${K}.toast.deleted`) })
    } catch (err) {
      setIsDeleteConfirmOpen(false)
      showError(mapAutoCreationRuleDeleteError(err))
    }
  }

  const isSaving = createRules.isPending || updateRule.isPending
  // 保存・削除の完了時に onClose するので、処理中に閉じて開き直したモーダルを
  // 完了時に閉じてしまわないよう、処理中は閉じさせない
  const isBusy = isSaving || deleteRule.isPending
  const closeUnlessBusy = (): void => {
    if (!isBusy) onClose()
  }

  const footer = (
    <HStack justify={isEdit ? 'space-between' : 'flex-end'} width="100%">
      {isEdit && (
        <OutlineDangerButton
          onClick={() => {
            setIsDeleteConfirmOpen(true)
          }}
        >
          {t('common:action.delete')}
        </OutlineDangerButton>
      )}
      <HStack gap={3}>
        <TertiaryButton disabled={isBusy} onClick={closeUnlessBusy}>
          {t('common:action.cancel')}
        </TertiaryButton>
        <PrimaryButton
          loading={isSaving}
          onClick={() => {
            // 欄のエラーは各欄の下に出すので onInvalid では何もしない
            void handleSubmit(onSubmit, () => undefined)()
          }}
        >
          {t('common:action.save')}
        </PrimaryButton>
      </HStack>
    </HStack>
  )

  return (
    <>
      <Modal
        footer={footer}
        isOpen={isOpen}
        onClose={closeUnlessBusy}
        size="lg"
        title={t(isEdit ? `${K}.modal.editTitle` : `${K}.modal.createTitle`)}
      >
        <VStack align="stretch" gap={4}>
          {banner !== undefined && (
            <Alert status="error" title={t(banner.messageKey)}>
              {banner.reloadable && (
                <AlertActions>
                  <TertiaryButton
                    onClick={() => {
                      setIsDiscardConfirmOpen(true)
                    }}
                    size="sm"
                  >
                    {t(`${K}.serverError.reload`)}
                  </TertiaryButton>
                </AlertActions>
              )}
            </Alert>
          )}

          <FieldRoot
            alignItems="stretch"
            invalid={errors.templateId !== undefined}
          >
            <FieldLabel htmlFor="acr-template">
              {t(`${K}.form.template`)}
              <RequiredMark />
            </FieldLabel>
            <Controller
              control={control}
              name="templateId"
              render={({ field }) => (
                <DsSelect
                  aria-label={t(`${K}.form.template`)}
                  inputId="acr-template"
                  invalid={errors.templateId !== undefined}
                  onBlur={field.onBlur}
                  onChange={(v) => {
                    field.onChange(v ?? '')
                    const picked = templates.find((tpl) => tpl.id === v)
                    // 編集では保存済みの起票リードを変えない
                    if (isEdit || picked === undefined) return
                    const lead = defaultLeadForSection(picked.section)
                    setValue('leadValue', String(lead.leadValue))
                    setValue('leadUnit', lead.leadUnit)
                  }}
                  options={templateOptions}
                  placeholder={t('common:form.selectPlaceholder')}
                  value={field.value === '' ? undefined : field.value}
                />
              )}
            />
            <FieldErrorText>{errors.templateId?.message}</FieldErrorText>
            <FieldHelperText>{t(`${K}.form.templateHint`)}</FieldHelperText>
          </FieldRoot>

          <FieldRoot
            alignItems="stretch"
            invalid={errors.areaCodes !== undefined}
          >
            <FieldLabel htmlFor="acr-area">
              {t(`${K}.form.area`)}
              <RequiredMark />
            </FieldLabel>
            <Controller
              control={control}
              name="areaCodes"
              render={({ field }) =>
                isEdit ? (
                  <DsSelect
                    aria-label={t(`${K}.form.area`)}
                    inputId="acr-area"
                    invalid={errors.areaCodes !== undefined}
                    onBlur={field.onBlur}
                    onChange={(v) => {
                      const code = enumSetValueAs(v)
                      field.onChange(code === undefined ? [] : [code])
                    }}
                    options={areaOptions}
                    value={field.value[0]}
                  />
                ) : (
                  <DsMultiSelect
                    aria-label={t(`${K}.form.area`)}
                    invalid={errors.areaCodes !== undefined}
                    onChange={(next) => {
                      field.onChange([...next].map(Number))
                    }}
                    options={areaOptions}
                    placeholder={t(`${K}.form.areaPlaceholder`)}
                    selected={new Set(field.value.map(String))}
                  />
                )
              }
            />
            <FieldErrorText>{errors.areaCodes?.message}</FieldErrorText>
            <FieldHelperText>
              {t(
                isEdit ? `${K}.form.areaHintEdit` : `${K}.form.areaHintCreate`,
              )}
            </FieldHelperText>
          </FieldRoot>

          <FieldRoot alignItems="stretch">
            <FieldLabel>
              {t(`${K}.form.trigger`)}
              <RequiredMark />
            </FieldLabel>
            {/* AC-WFT-021: イベント (AC-WFT-024〜038、P3) を提供するまでは出したまま選べなくする */}
            <AccessibleSegmentedControl
              aria-label={t(`${K}.form.trigger`)}
              name="auto-creation-rule-trigger"
              onSelect={() => undefined}
              options={[
                {
                  value: TRIGGER_SCHEDULE,
                  label: t(`${K}.trigger.schedule`),
                },
                {
                  value: TRIGGER_EVENT,
                  label: (
                    <ButtonTooltip
                      content={t(`${K}.trigger.eventUnavailable`)}
                      focusable
                    >
                      {t(`${K}.trigger.event`)}
                    </ButtonTooltip>
                  ),
                  disabled: true,
                },
              ]}
              selected={TRIGGER_SCHEDULE}
            />
            <FieldHelperText>{t(`${K}.form.triggerHint`)}</FieldHelperText>
          </FieldRoot>

          <FieldRoot alignItems="stretch">
            <FieldLabel>{t(`${K}.form.targetDays`)}</FieldLabel>
            <Text textStyle="body">{targetDaysText}</Text>
            <FieldHelperText>{t(`${K}.form.targetDaysHint`)}</FieldHelperText>
          </FieldRoot>

          <FieldRoot
            alignItems="stretch"
            invalid={errors.leadValue !== undefined}
          >
            <FieldLabel>
              {t(`${K}.form.lead`)}
              <RequiredMark />
            </FieldLabel>
            <HStack alignItems="center" gap={2}>
              <Trans
                components={{
                  value: (
                    <Input
                      width="88px"
                      aria-label={t(`${K}.form.lead`)}
                      inputMode="numeric"
                      min={0}
                      step={1}
                      type="number"
                      {...register('leadValue')}
                    />
                  ),
                  unit: (
                    <Controller
                      control={control}
                      name="leadUnit"
                      render={({ field }) => (
                        <DsSelect
                          width="137px"
                          aria-label={t(`${K}.form.leadUnit`)}
                          onBlur={field.onBlur}
                          onChange={(v) => {
                            const unit = enumSetValueAs(v)
                            if (unit !== undefined) field.onChange(unit)
                          }}
                          options={LEAD_UNIT_OPTIONS.map((unit) => ({
                            value: String(unit),
                            label: t(LEAD_UNIT_LABEL_KEYS[unit]),
                          }))}
                          value={field.value}
                        />
                      )}
                    />
                  ),
                }}
                i18nKey={`${K}.form.leadRow`}
              />
            </HStack>
            <FieldErrorText>{errors.leadValue?.message}</FieldErrorText>
          </FieldRoot>

          <FieldRoot
            alignItems="stretch"
            invalid={errors.fireTimeHm !== undefined}
          >
            <FieldLabel>
              {t(`${K}.form.fireTime`)}
              <RequiredMark />
            </FieldLabel>
            <Input
              width="140px"
              aria-label={t(`${K}.form.fireTime`)}
              type="time"
              {...register('fireTimeHm')}
            />
            <FieldErrorText>{errors.fireTimeHm?.message}</FieldErrorText>
          </FieldRoot>

          <FieldRoot alignItems="stretch">
            <FieldLabel htmlFor="acr-day-filter">
              {t(`${K}.form.dayFilter`)}
              <RequiredMark />
            </FieldLabel>
            <Controller
              control={control}
              name="dayFilter"
              render={({ field }) => (
                <DsSelect
                  aria-label={t(`${K}.form.dayFilter`)}
                  inputId="acr-day-filter"
                  onBlur={field.onBlur}
                  onChange={(v) => {
                    const filter = enumSetValueAs(v)
                    if (filter !== undefined) field.onChange(filter)
                  }}
                  options={DAY_FILTER_OPTIONS.map((filter) => ({
                    value: String(filter),
                    label: t(DAY_FILTER_LABEL_KEYS[filter]),
                  }))}
                  value={field.value}
                />
              )}
            />
            <FieldHelperText>{t(`${K}.form.dayFilterHint`)}</FieldHelperText>
          </FieldRoot>

          <FieldRoot alignItems="stretch">
            <Controller
              control={control}
              name="enabled"
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(details: {
                    checked: boolean | 'indeterminate'
                  }) => {
                    field.onChange(details.checked === true)
                  }}
                >
                  {t(`${K}.form.enabled`)}
                </Checkbox>
              )}
            />
            <FieldHelperText>{t(`${K}.form.enabledHint`)}</FieldHelperText>
          </FieldRoot>
        </VStack>
      </Modal>

      {isEdit && (
        <ConfirmDialog
          body={t(`${K}.delete.confirmBody`, {
            template: localizeBuiltinWorkflowText(row.template.name, t),
            area: convertAreaLabel(row.areaCode),
          })}
          confirmLabel={t('common:action.delete')}
          destructive
          isLoading={deleteRule.isPending}
          isOpen={isOpen && isDeleteConfirmOpen}
          onCancel={() => {
            setIsDeleteConfirmOpen(false)
          }}
          onConfirm={() => {
            void handleDelete()
          }}
          title={t(`${K}.delete.confirmTitle`)}
        />
      )}
      <ConfirmDialog
        body={t(`${K}.discard.confirmBody`)}
        confirmLabel={t(`${K}.discard.confirmLabel`)}
        destructive
        isOpen={isOpen && isDiscardConfirmOpen}
        onCancel={() => {
          setIsDiscardConfirmOpen(false)
        }}
        onConfirm={() => {
          setIsDiscardConfirmOpen(false)
          onReload()
        }}
        title={t(`${K}.discard.confirmTitle`)}
      />
    </>
  )
}
